import React from 'react'
import { Button } from 'react-bootstrap'
import { Box, Atom, Upload } from 'lucide-react'
import { modelCardStyles, buttonStyles } from '../styles'
import { useAppSelector } from '../../../../store/hooks'

import { useGetModelQuery, useDeployModelMutation } from '../../../../services/modelsApi'
import { selectSelectedModelId } from '../../../../store/slices/modelSlice'

interface ModelSelectorProps {
  onModelSelect: () => void
  onRunInference: () => void
  inferenceLoading: boolean
}

const ModelSelector: React.FC<ModelSelectorProps> = ({
  onModelSelect,
  onRunInference,
  inferenceLoading,
}) => {
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const { data: selectedInferenceModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })
  const [deployModel, { isLoading: isDeploying }] = useDeployModelMutation()

  const handleDeploy = async () => {
    if (selectedModelId) {
      try {
        await deployModel({ id: selectedModelId }).unwrap()
      } catch (error) {
        console.error('Failed to deploy model:', error)
      }
    }
  }

  return (
    <div className="tool-group mb-4">
      <h6 className="text-white mb-2">Model Assistance</h6>
      <div className="d-grid gap-2">
        {selectedInferenceModel ? (
          <div className="model-card mb-3" style={modelCardStyles.container}>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <div style={modelCardStyles.title}>{selectedInferenceModel.name}</div>
                <div style={modelCardStyles.subtitle}>
                  <Box size={14} style={{ opacity: 0.7 }} />
                  <span>Object Detection Model</span>
                </div>
              </div>
            </div>

            <div style={modelCardStyles.infoBox}>
              <div style={modelCardStyles.infoText}>
                <div style={modelCardStyles.infoRow}>
                  <span style={modelCardStyles.infoLabel}>Created:</span>
                  <span>{new Date(selectedInferenceModel.created_at).toLocaleDateString()}</span>
                </div>
                <div style={modelCardStyles.infoRow}>
                  <span style={modelCardStyles.infoLabel}>Status:</span>
                  <span style={modelCardStyles.statusText}>
                    {selectedInferenceModel.deployment.status}
                  </span>
                </div>
              </div>
            </div>

            <div style={modelCardStyles.infoRow}>
              {selectedInferenceModel.deployment.status === 'IN_SERVICE' ? (
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={onRunInference}
                  disabled={inferenceLoading}
                  style={buttonStyles.modelRun}
                  onMouseEnter={(e) => {
                    if (!inferenceLoading) {
                      Object.assign(e.currentTarget.style, buttonStyles.modelRunHover)
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!inferenceLoading) {
                      Object.assign(e.currentTarget.style, buttonStyles.modelRun)
                    }
                  }}
                >
                  <Atom size={18} className="me-2" />
                  {inferenceLoading ? 'Running...' : 'Run'}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={handleDeploy}
                  disabled={isDeploying}
                  style={buttonStyles.modelRun}
                  onMouseEnter={(e) => {
                    if (!isDeploying) {
                      Object.assign(e.currentTarget.style, buttonStyles.modelRunHover)
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDeploying) {
                      Object.assign(e.currentTarget.style, buttonStyles.modelRun)
                    }
                  }}
                >
                  <Upload size={18} className="me-2" />
                  {isDeploying ? 'Deploying...' : 'Deploy'}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <Button
            variant="primary"
            onClick={onModelSelect}
            className="d-flex align-items-center justify-content-center gap-2"
            style={buttonStyles.selectModelButton}
          >
            <Box size={18} />
            Select Model
          </Button>
        )}
      </div>
    </div>
  )
}

export default ModelSelector
